// src/pages/trading-portal/alerts/AlertsPage.js
// Price alert management page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Cancel as CancelIcon,
    NotificationsActive as NotificationsActiveIcon,
    PlaylistAddCheck as CheckAllIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    AlertCondition,
    AlertConditionLabels,
    AlertStatus,
    AlertStatusLabels,
    AlertStatusColors,
} from '../stockTradingEnums';

const AlertsPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // State
    const [alerts, setAlerts] = useState([]);
    const [symbols, setSymbols] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [tabFilter, setTabFilter] = useState('ALL');
    const [checkingAll, setCheckingAll] = useState(false);

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        symbol_id: null,
        condition: '',
        target_price: '',
        message: '',
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-alerts', 'view');
            const add = await hasPermission('tr-alerts', 'add');
            const del = await hasPermission('tr-alerts', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // Fetch symbols
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    }, []);

    // Fetch alerts
    const fetchAlerts = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({ page: page + 1, per_page: rowsPerPage });
            if (tabFilter !== 'ALL') params.append('status', tabFilter);

            const response = await axiosInstance.get(`/api/stock-trading/alerts?${params.toString()}`);

            if (response.data.success) {
                setAlerts(response.data.data || []);
                setTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch alerts');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch alerts');
        } finally {
            setLoading(false);
        }
    }, [canView, page, rowsPerPage, tabFilter]);

    useEffect(() => {
        if (canView) {
            fetchSymbols();
            fetchAlerts();
        }
    }, [fetchAlerts, fetchSymbols, canView]);

    // Handlers
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
    const handleTabChange = (event, newValue) => { setTabFilter(newValue); setPage(0); };

    const resetForm = () => {
        setFormData({ symbol_id: null, condition: '', target_price: '', message: '' });
        setFormErrors({});
    };

    const handleCreateOpen = () => { resetForm(); setCreateDialogOpen(true); };
    const handleCreateClose = () => { setCreateDialogOpen(false); resetForm(); };

    const handleFormChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.symbol_id) errors.symbol_id = 'Symbol is required';
        if (!formData.condition) errors.condition = 'Condition is required';
        if (!formData.target_price || isNaN(formData.target_price)) errors.target_price = 'Valid target price is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = async () => {
        if (!validateForm()) return;
        setSubmitting(true);

        try {
            const payload = {
                symbol_id: formData.symbol_id.id || formData.symbol_id,
                condition: formData.condition,
                target_price: parseFloat(formData.target_price),
                message: formData.message || undefined,
            };
            const response = await axiosInstance.post('/api/stock-trading/alerts', payload);

            if (response.data.success) {
                handleCreateClose();
                fetchAlerts();
            } else {
                setFormErrors({ submit: response.data.message || 'Failed to create alert' });
            }
        } catch (err) {
            setFormErrors({ submit: err.response?.data?.message || 'Failed to create alert' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (alert) => { setSelectedAlert(alert); setDeleteDialogOpen(true); };

    const handleDeleteConfirm = async () => {
        if (!selectedAlert) return;
        try {
            const response = await axiosInstance.delete(`/api/stock-trading/alerts/${selectedAlert.id}`);
            if (response.data.success) {
                fetchAlerts();
                setDeleteDialogOpen(false);
                setSelectedAlert(null);
            } else {
                setError(response.data.message || 'Failed to delete alert');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete alert');
        }
    };

    const handleCancelAlert = async (alert) => {
        try {
            await axiosInstance.put(`/api/stock-trading/alerts/${alert.id}`, { status: 'CANCELLED' });
            fetchAlerts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel alert');
        }
    };

    const handleCheckAllAlerts = async () => {
        setCheckingAll(true);
        try {
            await axiosInstance.post('/api/stock-trading/alerts/check');
            fetchAlerts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to check alerts');
        } finally {
            setCheckingAll(false);
        }
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view alerts.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" component="h1">Price Alerts</Typography>
                    <Chip label={totalCount} color="primary" size="small" />
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={checkingAll ? <CircularProgress size={18} /> : <CheckAllIcon />}
                        onClick={handleCheckAllAlerts}
                        disabled={checkingAll}
                    >
                        Check All
                    </Button>
                    {canAdd && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}
                            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                            Create Alert
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Tab Filter */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={tabFilter}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="All" value="ALL" />
                    <Tab label="Active" value="ACTIVE" />
                    <Tab label="Triggered" value="TRIGGERED" />
                    <Tab label="Expired" value="EXPIRED" />
                    <Tab label="Cancelled" value="CANCELLED" />
                </Tabs>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Alerts Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Condition</TableCell>
                                <TableCell align="right">Target Price</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Triggered</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : alerts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <NotificationsActiveIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No alerts found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                alerts.map((alert) => (
                                    <TableRow key={alert.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold">{alert.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={AlertConditionLabels[alert.condition] || alert.condition}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="medium">
                                                ${parseFloat(alert.target_price).toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {alert.message || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={AlertStatusLabels[alert.status] || alert.status}
                                                size="small"
                                                color={AlertStatusColors[alert.status] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {alert.created_at ? new Date(alert.created_at).toLocaleDateString() : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {alert.triggered_at ? new Date(alert.triggered_at).toLocaleString() : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                {alert.status === 'ACTIVE' && (
                                                    <Tooltip title="Cancel Alert">
                                                        <IconButton size="small" onClick={() => handleCancelAlert(alert)}>
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(alert)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>

            {/* Create Alert Dialog */}
            <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
                <DialogTitle>Create Price Alert</DialogTitle>
                <DialogContent>
                    {formErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{formErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbols}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={formData.symbol_id}
                                onChange={(e, newValue) => {
                                    setFormData(prev => ({ ...prev, symbol_id: newValue }));
                                    if (formErrors.symbol_id) setFormErrors(prev => ({ ...prev, symbol_id: null }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Symbol"
                                        error={!!formErrors.symbol_id}
                                        helperText={formErrors.symbol_id}
                                        required
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!formErrors.condition} required>
                                <InputLabel>Condition</InputLabel>
                                <Select value={formData.condition} label="Condition" onChange={handleFormChange('condition')}>
                                    {Object.entries(AlertCondition).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{AlertConditionLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                                {formErrors.condition && <Typography variant="caption" color="error">{formErrors.condition}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Target Price"
                                type="number"
                                value={formData.target_price}
                                onChange={handleFormChange('target_price')}
                                error={!!formErrors.target_price}
                                helperText={formErrors.target_price}
                                required
                                inputProps={{ step: '0.01', min: '0' }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message (optional)"
                                value={formData.message}
                                onChange={handleFormChange('message')}
                                placeholder="Alert notification message..."
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateClose} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSubmit} disabled={submitting}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Create Alert'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Alert</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this alert for <strong>{selectedAlert?.symbol}</strong>
                        {selectedAlert?.target_price ? ` (target: $${parseFloat(selectedAlert.target_price).toFixed(2)})` : ''}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AlertsPage;
